/**
 * Who may ACT on outreach — create, launch, edit, close, delete, every send,
 * feedback review, reports, charts, response archive/notes. Only an org_admin
 * of the workspace's OWNER org (or a system_admin): outreach is that
 * organization speaking in public. Everyone else who can open a workspace sees
 * outreach read-only. Documentation/OUTREACH_CONTRIBUTOR_READ_ONLY_PRD.md.
 *
 * Deliberately NOT `canManage`: shared-org admins and explicit workspace
 * owners hold it too, and the server refuses them
 * (backend/src/utils/outreachAccess.js — the server is authoritative; this
 * only decides what renders).
 *
 * The analytics page is a deep tree of components, so the decision is put in
 * context once at the top rather than threaded through every prop. The default
 * is `false`: a component rendered outside a provider shows no write controls.
 */
import React, { createContext, useContext } from 'react';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import { InfoIcon } from '../icons';
import { isOrgAdminOfOwnerOrg, useWorkspacePermissions } from '../../hooks/useWorkspacePermissions';

/** Error code the server returns when a non-admin reaches a write route. */
export const OUTREACH_ORG_ADMIN_REQUIRED = 'OUTREACH_ORG_ADMIN_REQUIRED';

/*
  A write can still reach the server from a stale page (a role changed after
  the page loaded). Say why in the server's words instead of each handler's
  generic "Failed to …". Registered once, on the web client only (never the
  shared client the native shells use), and it acts on this one error code.
*/
let refusalToastRegistered = false;
function registerRefusalToast() {
  if (refusalToastRegistered) return;
  refusalToastRegistered = true;
  api.interceptors.response.use(undefined, (error) => {
    const data = error?.response?.data;
    if (error?.response?.status === 403 && data?.code === OUTREACH_ORG_ADMIN_REQUIRED) {
      toast.error(data.error || 'Outreach changes require an organization admin', { id: OUTREACH_ORG_ADMIN_REQUIRED });
    }
    return Promise.reject(error);
  });
}
registerRefusalToast();

/** Resolve the decision for one workspace. `false` until permissions load. */
export function useOutreachActAccess(workspaceId?: string | null): { canAct: boolean; loading: boolean } {
  const { permissions, loading } = useWorkspacePermissions(workspaceId ?? undefined);
  return { canAct: !loading && isOrgAdminOfOwnerOrg(permissions), loading };
}

const OutreachAccessContext = createContext(false);

export const OutreachAccessProvider: React.FC<{ canAct: boolean; children: React.ReactNode }> = ({
  canAct,
  children,
}) => <OutreachAccessContext.Provider value={canAct}>{children}</OutreachAccessContext.Provider>;

/** Inside the analytics page: may the current user act on this outreach? */
export function useCanActOnOutreach(): boolean {
  return useContext(OutreachAccessContext);
}

/**
 * The quiet explanation shown to read-only viewers, at the top of the Outreach
 * tab and the analytics page. Not dismissible and not an alert — it explains a
 * state; it does not report a problem.
 */
export const OutreachReadOnlyNotice: React.FC<{ className?: string }> = ({ className = '' }) => (
  <p
    className={`flex items-start gap-2 rounded-lg border border-border-light bg-primary-50 px-3 py-2 text-sm text-primary-800 dark:border-border-dark dark:bg-primary-900/20 dark:text-primary-200 ${className}`}
    data-testid="outreach-read-only-notice"
  >
    <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
    <span>
      You can view outreach in this workspace. Creating, launching, sending and editing are done by your
      organization&apos;s admins.
    </span>
  </p>
);
